import { useState } from "react";
import { ClassNamesConfig, GroupBase } from "react-select";
import AsyncSelect from "react-select/async";

import { Label } from "@/components/ui/label";
import {
  SearchIndicator,
  createClassNames,
} from "@/components/ui/react-select";
import { getOrganisations } from "@/features/organisations/api/get-organisations";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { OrganisationSelectOption } from "@/features/session/components/organisation-select-option";
import { OrganisationOption } from "@/features/session/types/organisation-option";
import { cn } from "@/lib/utils";
import { PublicEvent } from "@classview/shared-types";
import { useQueryClient } from "@tanstack/react-query";

import { useAddOrganisationToEvent } from "../api/add-organisation-to-event";
import { getEventQueryKey } from "../api/get-event";

type AddOrganisationToEventFormProps = {
  event: PublicEvent;
};

export function AddOrganisationToEventForm({
  event,
}: AddOrganisationToEventFormProps) {
  const { organisation } = useOrganisation();
  const [value, setValue] = useState<string>("");
  const queryClient = useQueryClient();
  const { mutateAsync: addOrganisationToEvent, isPending } =
    useAddOrganisationToEvent({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getEventQueryKey(event.id) });
      },
    });
  const addOrganisation = async (addedOrganisation: OrganisationOption) => {
    try {
      await addOrganisationToEvent({
        orgId: organisation.id,
        eventId: event.id,
        payload: {
          organisationId: addedOrganisation.id,
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const organisationsError = null;
  //   const organisationsError = form.formState.errors.invitedOrganisations;
  const reactSelectClassNames = createClassNames({
    control: () => {
      return cn(!!organisationsError && "border-destructive");
    },
  });

  //   const selectedOrganisations = form.getValues("invitedOrganisations");
  const selectedOrganisations: OrganisationOption[] =
    event.session.invitedOrganisations.map((organisation) => ({
      id: organisation.id,
      name: organisation.name,
      status: organisation.status,
    }));

  const loadOptions = async (input: string): Promise<OrganisationOption[]> => {
    const organisations = await getOrganisations(input);

    const filteredOrganisations = organisations.filter(
      (orgItem) =>
        !selectedOrganisations.some((o) => o.id === orgItem.id) &&
        orgItem.id !== organisation.id,
    );

    return filteredOrganisations.map((organisation) => ({
      id: organisation.id,
      name: organisation.name,
      status: "NEW",
    }));
  };

  return (
    <div className="mb-4 space-y-4">
      <div className="space-y-2">
        <Label>Add Organisation</Label>
        <AsyncSelect<OrganisationOption>
          unstyled
          components={{
            DropdownIndicator: SearchIndicator,
            ClearIndicator: () => null,
            Option: OrganisationSelectOption,
            IndicatorSeparator: () => null,
          }}
          //   ref={form.register("roomSystems").ref}
          classNames={
            reactSelectClassNames as ClassNamesConfig<
              OrganisationOption,
              false,
              GroupBase<OrganisationOption>
            >
          }
          placeholder="Type organisation name..."
          noOptionsMessage={() => "Type organisation name..."}
          loadOptions={loadOptions}
          isDisabled={isPending}
          inputValue={value}
          onInputChange={(value) => {
            setValue(value);
          }}
          value={null}
          getOptionLabel={(option) => option.name}
          onChange={(value) => {
            if (value) {
              addOrganisation(value);
              setValue(""); // Clear the input once added
              return null; // Prevent adding to internal state
            }
          }}
        />
        {/* {organisationsError && (
          <p className="text-sm font-medium text-destructive">
            {organisationsError.message}
          </p>
        )} */}
        <p className="text-sm text-muted-foreground">
          Organisation admins will receive an invitation, where they can assign
          users and room systems to this event.
        </p>
      </div>
    </div>
  );
}
